/**
 * Drawer layout — sidebar repliable.
 *
 * data-collapsible absent : sidebar fixe en desktop, tiroir en mobile.
 * data-collapsible="true" : la sidebar peut être masquée en desktop (case drawer-toggle cochée).
 * data-collapsible="rail" : la sidebar se réduit aux icônes en desktop. État dans
 *   data-drawer-state="open|rail" (observé : l'application peut l'écrire), API
 *   DrawerLayout.setState / getState, événement ds-drawer:change. Seul un clic de
 *   l'utilisateur est mémorisé (localStorage 'ds-drawer-state', désactivable par
 *   data-drawer-remember="false").
 * Mobile (≤ 768px) : tiroir plein écran avec overlay dans tous les cas.
 */
import { initTooltips } from '../../components/tooltip/tooltip.js';

const STORAGE_KEY = 'ds-drawer-state';
const MOBILE_QUERY = '(max-width: 768px)';
const RAIL_STATES = ['open', 'rail'];
const controllers = new WeakMap();
let uid = 0;

const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

export function initDrawer(container = document) {
  const layouts = [...container.querySelectorAll('.drawer-layout')];
  if (container.matches?.('.drawer-layout')) layouts.unshift(container);
  layouts.forEach(setupLayout);

  if (typeof window !== 'undefined') window.DrawerLayout = { setState, getState };
}

/** État courant : 'open' | 'rail' (mode rail), 'open' | 'closed' (mode true). */
export function getState(layout) {
  const ctrl = controllers.get(layout);
  return ctrl ? ctrl.getState() : null;
}

/** Force l'état depuis l'application. { persist: true } pour le mémoriser comme un choix utilisateur. */
export function setState(layout, state, { persist = false } = {}) {
  controllers.get(layout)?.setState(state, { persist });
}

function setupLayout(layout) {
  if (controllers.has(layout)) return;
  const checkbox = layout.querySelector('.drawer-toggle');
  const sidebar = layout.querySelector('.drawer-sidebar');
  const toggles = [...layout.querySelectorAll('.drawer-toggle-btn')];
  if (!checkbox || !sidebar) return;

  const mode = layout.dataset.collapsible === 'rail' ? 'rail' : layout.dataset.collapsible === 'true' ? 'true' : 'fixed';
  const remember = layout.dataset.drawerRemember !== 'false';
  sidebar.id ||= `drawer-sidebar-${++uid}`;
  let lastEmitted = null; // évite un double ds-drawer:change (applyRail + observateur d'attribut)

  // ---------- État ----------
  function readStored() {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return RAIL_STATES.includes(v) ? v : null;
    } catch (e) { return null; }
  }

  function getRailState() {
    return RAIL_STATES.includes(layout.dataset.drawerState) ? layout.dataset.drawerState : 'open';
  }

  function applyRail(state, { persist = false, emit = true } = {}) {
    if (!RAIL_STATES.includes(state)) return;
    layout.dataset.drawerState = state;
    if (persist && remember) {
      try { localStorage.setItem(STORAGE_KEY, state); } catch (e) { /* stockage indisponible */ }
    }
    refresh();
    if (emit) emitChange(state);
    else lastEmitted = state;
  }

  function emitChange(state) {
    if (state === lastEmitted) return;
    lastEmitted = state;
    layout.dispatchEvent(new CustomEvent('ds-drawer:change', { bubbles: true, detail: { state } }));
  }

  const ctrl = {
    getState() {
      if (mode === 'rail') return getRailState();
      if (mode === 'true') return checkbox.checked && !isMobile() ? 'closed' : 'open';
      return 'open';
    },
    setState(state, { persist = false } = {}) {
      if (mode === 'rail') return applyRail(state, { persist });
      if (mode === 'true' && ['open', 'closed'].includes(state)) {
        checkbox.checked = state === 'closed';
        refresh();
        layout.dispatchEvent(new CustomEvent('ds-drawer:change', { bubbles: true, detail: { state } }));
      }
    },
  };
  controllers.set(layout, ctrl);

  // ---------- Bouton de bascule accessible ----------
  // Le <label for="drawer-toggle"> n'est pas focalisable : on en fait un vrai bouton.
  toggles.forEach((btn) => {
    btn.setAttribute('aria-controls', sidebar.id);
    if (btn.tagName === 'LABEL') {
      btn.setAttribute('role', 'button');
      btn.tabIndex = 0;
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click(); }
      });
    }
    btn.addEventListener('click', (e) => {
      if (mode !== 'rail' || isMobile()) return; // mobile / mode true : bascule native de la case
      e.preventDefault();
      applyRail(getRailState() === 'open' ? 'rail' : 'open', { persist: true });
    });
  });

  function updateToggles() {
    let expanded = true;
    let label = '';
    if (isMobile()) {
      expanded = checkbox.checked;
      label = expanded ? 'Fermer le menu' : 'Ouvrir le menu';
    } else if (mode === 'rail') {
      expanded = getRailState() === 'open';
      label = expanded ? 'Réduire le menu' : 'Déplier le menu';
    } else if (mode === 'true') {
      expanded = !checkbox.checked;
      label = expanded ? 'Masquer le menu' : 'Afficher le menu';
    }
    toggles.forEach((btn) => {
      btn.setAttribute('aria-expanded', String(expanded));
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  // ---------- Tooltips automatiques en rail ----------
  const railCandidates = () => sidebar.querySelectorAll('.menu__link, .menu__trigger, .btn:has(> .drawer-rail-label), [data-rail-tooltip]');

  function updateTooltips() {
    const railActive = mode === 'rail' && !isMobile() && getRailState() === 'rail';
    railCandidates().forEach((el) => {
      const auto = el.dataset.railAuto === 'true';
      if (railActive) {
        if (el.classList.contains('has-tooltip') && !auto) return; // tooltip posé par l'auteur : conservé
        const text = el.dataset.railTooltip
          || el.querySelector('.menu__text, .drawer-rail-label')?.textContent.trim()
          || el.getAttribute('aria-label')
          || el.textContent.trim();
        if (!text) return;
        el.classList.add('has-tooltip');
        el.dataset.tooltip = text;
        el.dataset.placement = 'right';
        el.dataset.railAuto = 'true';
      } else if (auto) {
        el.classList.remove('has-tooltip', 'has-tooltip--js');
        delete el.dataset.tooltip;
        delete el.dataset.placement;
        delete el.dataset.railAuto;
      }
    });
    if (railActive) initTooltips(sidebar);
  }

  // Élément sans icône : initiales du libellé, affichées en rail à la place de l'icône
  function initials(text) {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '';
    if (words.length === 1) return words[0].slice(0, 2).replace(/^./, (c) => c.toUpperCase());
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  function updateInitials() {
    railCandidates().forEach((el) => {
      if (el.querySelector('svg, img')) { delete el.dataset.railInitials; return; }
      const text = el.dataset.railTooltip
        || el.querySelector('.menu__text, .drawer-rail-label')?.textContent
        || el.textContent;
      el.dataset.railInitials = initials(text || '');
    });
  }

  function refresh() {
    updateToggles();
    updateInitials();
    updateTooltips();
  }

  // ---------- Sous-menus en rail : un clic déplie la sidebar et ouvre le sous-menu ----------
  sidebar.addEventListener('click', (e) => {
    const summary = e.target.closest('summary');
    if (!summary || mode !== 'rail' || isMobile() || getRailState() !== 'rail') return;
    const details = summary.parentElement;
    e.preventDefault();
    applyRail('open', { persist: true });
    details.open = true;
    summary.focus();
  }, true);

  // ---------- Liens : en mobile, le tiroir se referme après navigation ----------
  layout.querySelectorAll('.drawer-sidebar a, .search-results a').forEach((link) => {
    link.addEventListener('click', () => {
      if (isMobile()) { checkbox.checked = false; refresh(); }
    });
  });
  checkbox.addEventListener('change', refresh);

  // ---------- Desktop / mobile ----------
  function onViewport() {
    if (!isMobile() && mode === 'fixed') checkbox.checked = false;
    if (!isMobile() && mode === 'rail') checkbox.checked = false; // la case ne sert qu'au tiroir mobile
    refresh();
  }
  window.matchMedia(MOBILE_QUERY).addEventListener('change', onViewport);

  // L'application peut écrire data-drawer-state directement
  new MutationObserver(() => {
    if (mode !== 'rail') return;
    refresh();
    emitChange(getRailState());
  }).observe(layout, { attributes: true, attributeFilter: ['data-drawer-state'] });

  // ---------- État initial ----------
  if (mode === 'rail') {
    const initial = RAIL_STATES.includes(layout.dataset.drawerState)
      ? layout.dataset.drawerState
      : (remember && readStored()) || (checkbox.checked && !isMobile() ? 'rail' : 'open');
    if (!isMobile()) checkbox.checked = false;
    applyRail(initial, { emit: false });
  } else {
    onViewport();
  }
}
